import { useId, type FormEvent } from 'react';
import { Button } from '@shared/ui/Button';
import {
  AmountField,
  ButtonGroup,
  FormBox,
  FormErrorText,
  FormGrid,
  FormTitle,
  Input,
  Select,
} from './styles';

interface CreateApprovalFormProps {
  counterparty: string;
  setCounterparty: (v: string) => void;
  amount: string;
  setAmount: (v: string) => void;
  /** Сумма не разобрана: что исправить в поле. */
  amountError: string | null;
  priority: string;
  setPriority: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  budgetStatus: string;
  setBudgetStatus: (v: string) => void;
  initiator: string;
  setInitiator: (v: string) => void;
  purpose: string;
  setPurpose: (v: string) => void;
  isCreating: boolean;
  errorMsg: string | null;
  onSubmit: (e: FormEvent) => void;
  onClose: () => void;
}

export function CreateApprovalForm({
  counterparty,
  setCounterparty,
  amount,
  setAmount,
  amountError,
  priority,
  setPriority,
  category,
  setCategory,
  budgetStatus,
  setBudgetStatus,
  initiator,
  setInitiator,
  purpose,
  setPurpose,
  isCreating,
  errorMsg,
  onSubmit,
  onClose,
}: CreateApprovalFormProps) {
  const id = useId();
  return (
    <FormBox as="form" onSubmit={onSubmit}>
      <FormTitle>Новая заявка на оплату</FormTitle>
      <FormGrid>
        <Input
          placeholder="Контрагент / Получатель"
          value={counterparty}
          onChange={(e) => setCounterparty(e.target.value)}
          required
        />
        <AmountField>
          <Input
            type="text"
            inputMode="decimal"
            placeholder="Сумма к оплате, ₽"
            aria-label="Сумма к оплате, ₽"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            aria-invalid={amountError ? true : undefined}
            aria-describedby={amountError ? `${id}-amount-error` : undefined}
            required
          />
          {amountError && <FormErrorText id={`${id}-amount-error`}>{amountError}</FormErrorText>}
        </AmountField>
        <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="critical">Критичный приоритет (кредиты/лизинг/налоги)</option>
          <option value="high">Высокий (ключевые поставки СМР)</option>
          <option value="normal">Плановый (в графике)</option>
          <option value="low">Низкий (можно отложить)</option>
        </Select>
        <Select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="materials">МТО и материалы</option>
          <option value="fuel">ГСМ (топливо и масла)</option>
          <option value="equipment">Силовое оборудование</option>
          <option value="subcontract">Субподряд (СМР)</option>
          <option value="logistics">Транспорт и логистика</option>
          <option value="payroll">ФОТ, вахтовые и суточные</option>
          <option value="leasing">Лизинговый платёж</option>
          <option value="debt_service">Погашение кредита / %</option>
          <option value="taxes">Налоги и взносы</option>
          <option value="overhead">Накладные расходы</option>
        </Select>
        <Select value={budgetStatus} onChange={(e) => setBudgetStatus(e.target.value)}>
          <option value="within_limit">В лимите БДДС</option>
          <option value="over_limit">Сверх лимита статьи БДДС</option>
          <option value="unbudgeted">Внебюджетная заявка</option>
        </Select>
        <Input
          placeholder="Инициатор (ФИО / служба)"
          value={initiator}
          onChange={(e) => setInitiator(e.target.value)}
        />
        <Input
          placeholder="Назначение и основание оплаты"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          required
        />
      </FormGrid>
      {errorMsg && <FormErrorText role="alert">{errorMsg}</FormErrorText>}
      <ButtonGroup>
        <Button $size="sm" type="submit" disabled={isCreating}>
          {isCreating ? 'Сохранение…' : 'Добавить на акцепт'}
        </Button>
        <Button
          $size="sm"
          $variant="ghost"
          type="button"
          onClick={onClose}
        >
          Отмена
        </Button>
      </ButtonGroup>
    </FormBox>
  );
}
